import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Routes,
  Route,
  useMatch,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { db } from "./firebase/config";
import {
  collection,
  onSnapshot,
  query,
  doc,
  updateDoc,
} from "firebase/firestore";
import { AuthProvider, useAuth } from "./context/AuthContext";

import { initMercadoPago } from "@mercadopago/sdk-react";

import { Toaster } from "react-hot-toast";

// Estilos e Configurações
import "./assets/styles/globals.css";
import logoRepique from "./assets/images/logorepique.jpg";
import logoRepique2 from "./assets/images/logorepique2.png";
import logoRepique3 from "./assets/images/logorepique3.png";
import logoRepiqueEscuro from "./assets/images/logorepiqueescuro.png";
import logoRepiquePreto from "./assets/images/logorepiquepreto.png";
import { numeroWhatsApp } from "./data/produto.js";

//opa
// Componentes
import Header from "./components/common/Header/Header";
import Footer from "./components/common/Footer/Footer";
import BotaoWhats from "./components/common/Button/Button";
import Carrinho from "./components/common/Cart/Cart";
import ProdutoDetalhado from "./pages/DetalheProduto/DetalheProduto";
import Catalogo from "./components/product/ProductList/ProductList.jsx";
import SuaEspora from "./pages/MonteSuaEspora/MonteSuaEspora";
import Contato from "./pages/Contato/Contato";
import PgProdutos from "./pages/Produtos/Produtos";
import { Login } from "./pages/SystemConta/Login/Login.jsx";
import { Dashboard } from "./pages/Admin/Dashboard/Dashboard";
import { Register } from "./pages/SystemConta/Register/Register.jsx";
import { Checkout } from "./pages/Checkout/Checkout.jsx";
import { UserProfile } from "./pages/UserProfile/UserProfile.jsx";
import { ForgotPassword } from "./pages/SystemConta/ForgotPassword/ForgotPassword.jsx";

if (process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY) {
  initMercadoPago(process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY);
}

// --- PROTETOR DE ROTA ---
const PrivateRoute = ({ children }) => {
  const { user, userData, loading } = useAuth();

  // 1. Enquanto o Firebase está buscando os dados, mostramos uma tela de espera
  if (loading) {
    return (
      <div className="loading-screen">Verificando permissões de acesso...</div>
    );
  }

  // 2. Verifica se o usuário está logado E se o campo 'role' no Firestore é 'admin'
  const isAdmin = user && userData?.role === "admin";

  // 3. Se não estiver logado, manda para o Login
  if (!user) {
    return <Navigate to="/login" />;
  }

  // 4. Se estiver logado mas NÃO for admin, manda para a Home (ou uma página de erro)
  if (!isAdmin) {
    console.warn(
      "Acesso negado: Usuário não possui privilégios de administrador.",
    );
    return <Navigate to="/" />;
  }

  // 5. Se for admin, libera o acesso aos filhos (Dashboard)
  return children;
};

function AppContent() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartItems, setCartItems] = useState([]);
  const [activeFilter, setActiveFilter] = useState("todos");
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const { user, userData, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdminPage =
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/register") ||
    location.pathname.startsWith("/dashboard");

  // --- BUSCA PRODUTOS REAL-TIME ---
  useEffect(() => {
    const q = query(collection(db, "produtos"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setProducts(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // --- LÓGICA DE PRODUTO ATUAL (REINSERIDA PARA CORRIGIR O ERRO) ---
  const matchProduct = useMatch("/produto/:id");
  const produtoId = matchProduct?.params?.id || null;

  const produtoAtual = useMemo(
    () => (produtoId ? products.find((p) => p.id === produtoId) : null),
    [produtoId, products],
  );

  useEffect(() => {
    setGalleryIndex(0);
  }, [produtoId]);

  const visibleProducts = useMemo(
    () => products.filter((p) => Number(p.estoque) > 0),
    [products],
  );

  const categoriasDinâmicas = useMemo(() => {
    if (visibleProducts.length === 0)
      return [{ chave: "todos", label: "Todos" }];

    const únicas = Array.from(
      new Set(visibleProducts.map((p) => p.categoria).filter(Boolean)),
    );

    return [
      { chave: "todos", label: "Todos" },
      ...únicas.map((cat) => {
        const item = visibleProducts.find((p) => p.categoria === cat);
        return {
          chave: cat,
          label: item?.categoriaLabel || cat,
        };
      }),
    ];
  }, [visibleProducts]);

  // --- CARREGAMENTO DO CARRINHO (Firestore -> App) ---
  useEffect(() => {
    if (!authLoading) {
      if (user && userData?.carrinho) {
        setCartItems(userData.carrinho);
      } else if (!user) {
        setCartItems([]);
      }
    }
  }, [userData, authLoading, user]);

  useEffect(() => {
    if (loading || products.length === 0) return;

    setCartItems((prev) => {
      const sincronizado = prev.reduce((acc, item) => {
        const produtoAtual = products.find((p) => p.id === item.id);

        if (!produtoAtual) {
          return acc;
        }

        const estoqueAtual = Number(produtoAtual.estoque);
        if (!Number.isFinite(estoqueAtual) || estoqueAtual <= 0) {
          return acc;
        }

        const quantidadeAtual = Math.min(
          Math.max(1, Number(item.quantidade || 1)),
          estoqueAtual,
        );

        acc.push({
          ...produtoAtual,
          quantidade: quantidadeAtual,
        });

        return acc;
      }, []);

      const mudou = JSON.stringify(prev) !== JSON.stringify(sincronizado);

      if (mudou) {
        saveCartToFirestore(sincronizado);
      }

      return sincronizado;
    });
  }, [products, loading]);

  useEffect(() => {
    // Volta o scroll para o topo quando muda de página
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/login" && location.state?.message) {
      showToast(location.state.message);

      // Limpa o estado para o toast não reaparecer se o usuário atualizar a página
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // --- FUNÇÃO AUXILIAR DE SINCRONIZAÇÃO (Manual) ---
  const saveCartToFirestore = async (newCart) => {
    if (user) {
      try {
        const userRef = doc(db, "users", user.uid);
        const cleanCart = JSON.parse(JSON.stringify(newCart));
        await updateDoc(userRef, { carrinho: cleanCart });
      } catch (error) {
        console.error("Erro ao salvar no Firestore:", error);
      }
    }
  };

  // --- FUNÇÕES DE CARRINHO E TOAST ---
  const showToast = (msg) => {
    setToastMessage(msg);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 3000);
  };

  const isInCart = (id) => cartItems.some((item) => item.id === id);

  const toggleCartItem = (id) => {
    if (!user) {
      showToast("Faça login para adicionar ao carrinho");
      navigate("/login");
      return;
    }

    const produto = products.find((item) => item.id === id);
    if (!produto) return;

    if (Number(produto.estoque) <= 0) {
      showToast(
        "Este produto está sem estoque e não pode ser adicionado ao carrinho.",
      );
      return;
    }

    setCartItems((prev) => {
      const existe = prev.some((item) => item.id === id);
      let novoCarrinho;
      if (existe) {
        showToast(`${produto.nome} removido`);
        novoCarrinho = prev.filter((item) => item.id !== id);
      } else {
        showToast(`${produto.nome} adicionado`);
        novoCarrinho = [...prev, { ...produto, quantidade: 1 }];
      }
      saveCartToFirestore(novoCarrinho);
      return novoCarrinho;
    });
  };

  const alterarQuantidade = (id, delta) => {
    setCartItems((prev) => {
      const produto = products.find((item) => item.id === id);

      if (!produto) {
        const novoCarrinho = prev.filter((item) => item.id !== id);
        saveCartToFirestore(novoCarrinho);
        return novoCarrinho;
      }

      const estoqueAtual = Number(produto.estoque);
      if (!Number.isFinite(estoqueAtual) || estoqueAtual <= 0) {
        const novoCarrinho = prev.filter((item) => item.id !== id);
        saveCartToFirestore(novoCarrinho);
        showToast(
          `O produto "${produto.nome}" saiu do estoque e foi removido do carrinho.`,
        );
        return novoCarrinho;
      }

      const itemAtual = prev.find((item) => item.id === id);
      const novaQuantidade = Math.max(1, (itemAtual?.quantidade || 1) + delta);

      if (delta > 0 && novaQuantidade > estoqueAtual) {
        showToast(
          `Quantidade indisponível. Estoque atual de ${produto.nome}: ${produto.estoque}.`,
        );
        return prev;
      }

      const novoCarrinho = prev.map((item) =>
        item.id === id
          ? {
              ...produto,
              quantidade: Math.max(1, item.quantidade + delta),
            }
          : item,
      );

      saveCartToFirestore(novoCarrinho);
      return novoCarrinho;
    });
  };

  const removerItem = (id) => {
    setCartItems((prev) => {
      const novoCarrinho = prev.filter((i) => i.id !== id);
      saveCartToFirestore(novoCarrinho);
      return novoCarrinho;
    });
  };

  const limparCarrinho = () => {
    setCartItems([]);
    saveCartToFirestore([]);
  };

  if (loading && !isAdminPage) return <div className="loading-app">...</div>;

  return (
    <div className="App">
      {!isAdminPage && (
        <Header
          onOpenCart={() => setCartOpen(true)}
          mobileMenuOpen={mobileMenuOpen}
          onToggleMobile={() => setMobileMenuOpen((prev) => !prev)}
        />
      )}

      <main
        className={isAdminPage ? "admin-layout" : "page-transition-wrapper"}
      >
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route
              path="/"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <>
                    <section className="hero" id="inicio">
                      <div className="hero-fundo" />
                      <div className="hero-grade" />
                      <div className="hero-conteudo">
                        {/* <div className="hero-etiqueta">
                          Feito em Lagoa Vermelha - RS
                        </div> */}
                        <img
                          className="hero-logo"
                          src={logoRepiqueEscuro}
                          alt="Logomarca Repique Esporas"
                        />
                        <div className="hero-acoes">
                          <button
                            className="btn-primario"
                            onClick={() => navigate("/produtos")}
                          >
                            Ver Catálogo
                          </button>
                          <button
                            className="btn-primario"
                            onClick={() => navigate("/sua-espora")}
                          >
                            Personalize sua Espora
                          </button>
                          <button
                            className="btn-primario"
                            onClick={() => navigate("/contato")}
                          >
                            Fale com a gente
                          </button>
                        </div>
                      </div>
                      <div className="hero-linha-decorativa" />
                    </section>

                    <section className="secao-destaque" id="sobre">
                      <div className="destaque-conteudo">
                        <h2 className="destaque-titulo">
                          Esporas personalizadas, cuias com viróla de alpaca,
                          bombas e muito mais.
                        </h2>
                        <p className="destaque-texto">
                          A Repique Esporas vende esporas artesanais, bombas,
                          cuias e acessórios de alta qualidade.
                        </p>
                      </div>
                    </section>

                    <Catalogo
                      products={visibleProducts}
                      categories={categoriasDinâmicas}
                      activeFilter={activeFilter}
                      onFilterChange={setActiveFilter}
                      onOpenProduct={(id) => navigate(`/produto/${id}`)}
                      onToggleCart={toggleCartItem}
                      isInCart={isInCart}
                    />
                  </>
                </motion.div>
              }
            />

            <Route
              path="/produtos"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <PgProdutos
                    onOpenProduct={(id) => navigate(`/produto/${id}`)}
                  />
                </motion.div>
              }
            />

            <Route
              path="/produto/:id"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <ProdutoDetalhado
                    product={produtoAtual}
                    galleryIndex={galleryIndex}
                    onGalleryChange={setGalleryIndex}
                    onToggleCart={toggleCartItem}
                    isInCart={isInCart(produtoId)}
                    onNavigateHome={() => navigate("/produtos")}
                    relatedProducts={visibleProducts.filter(
                      (p) =>
                        p.categoria === produtoAtual?.categoria &&
                        p.id !== produtoId,
                    )}
                    onOpenProduct={(id) => navigate(`/produto/${id}`)}
                  />
                </motion.div>
              }
            />

            <Route
              path="/sua-espora"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <SuaEspora />
                </motion.div>
              }
            />
            <Route
              path="/contato"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <Contato />
                </motion.div>
              }
            />
            <Route
              path="/login"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <Login />
                </motion.div>
              }
            />
            <Route
              path="/register"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <Register />
                </motion.div>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <ForgotPassword />
                </motion.div>
              }
            />
            <Route
              path="/dashboard"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                </motion.div>
              }
            />
            <Route
              path="/checkout"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <Checkout
                    cartItems={cartItems}
                    onClearCart={limparCarrinho}
                    products={products}
                  />
                </motion.div>
              }
            />
            <Route
              path="/meu-perfil"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <UserProfile />
                </motion.div>
              }
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </AnimatePresence>
      </main>

      {!isAdminPage && (
        <>
          <Footer />
          <BotaoWhats
            onClick={() =>
              window.open(`https://wa.me/${numeroWhatsApp}`, "_blank")
            }
          />
        </>
      )}

      <Carrinho
        open={cartOpen}
        cartItems={cartItems}
        onClose={() => setCartOpen(false)}
        onRemoveItem={removerItem}
        onQuantityChange={alterarQuantidade}
        onClearCart={limparCarrinho}
        onFinalize={() => {
          setCartOpen(false);
          navigate("/checkout");
        }}
      />

      <div className={`toast ${toastVisible ? "ativo" : ""}`}>
        {toastMessage}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#0a0a0a",
            color: "#fff",
            border: "1px solid #1a1a1a",
            fontFamily: "Montserrat, sans-serif",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "#d4af37", // Dourado Repique no ícone de sucesso
              secondary: "#000",
            },
          },
        }}
      />
    </AuthProvider>
  );
}
